import { useRef, useState } from 'react';
import ReactSelect, { MultiValue, StylesConfig } from 'react-select';
import { Pencil, Save, X } from 'lucide-react';

import { Button } from 'components/ui/button';
import { MouseTooltip } from 'components/ui/MouseTooltip';
import type {
  YardGateAppointmentStatus,
  YardGateDockOption,
} from 'contracts/YardGate';
import type { YardGateEntry } from '../../adapter';
import { editableCellSelectStyle } from './editableSelectStyle';

export interface DockOption extends YardGateDockOption {}

const dockSelectStyle = editableCellSelectStyle as unknown as StylesConfig<
  DockOption,
  true
>;

interface DockCellProps {
  entry: YardGateEntry;
  options: DockOption[];
  isBusy: boolean;
  onSave: (docks: number[]) => Promise<boolean>;
}

const editableStatuses = new Set<YardGateAppointmentStatus>([
  'PENDING',
  'AWAITING_RELEASE',
  'ENTRY_AUTHORIZED',
  'EXIT_AUTHORIZED',
]);

export const DockCell = ({ entry, options, isBusy, onSave }: DockCellProps) => {
  const [editing, setEditing] = useState(false);
  const [selection, setSelection] = useState<number[]>(
    entry.docks.map(({ id }) => id)
  );
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null);
  const previousSelection = useRef<number[]>(entry.docks.map(({ id }) => id));
  const canEdit = editableStatuses.has(entry.status);
  const canShowDockTooltip =
    entry.status !== 'CANCELLED' && entry.status !== 'NO_SHOW';

  if (!editing) {
    const dockNames = entry.docks.map(
      ({ id, name }) =>
        options.find(({ value }) => value === id)?.label ?? name
    );
    return (
      <>
        <span
          className="inline-flex max-w-[220px] items-center gap-1"
          onMouseMove={(event) =>
            setHover({ x: event.clientX, y: event.clientY })
          }
          onMouseLeave={() => setHover(null)}
        >
          <span className="truncate">{dockNames.join(', ') || '—'}</span>
          {canEdit ? (
            <button
              type="button"
              aria-label={`Editar Doca de ${entry.plate}`}
              title="Editar Doca"
              disabled={isBusy}
              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 disabled:opacity-50"
              onClick={() => {
                const current = entry.docks.map(({ id }) => id);
                previousSelection.current = current;
                setSelection(current);
                setHover(null);
                setEditing(true);
              }}
            >
              <Pencil size={14} aria-hidden="true" />
            </button>
          ) : null}
        </span>
        {hover && canShowDockTooltip ? (
          <MouseTooltip
            x={hover.x}
            y={hover.y}
            title="Docas"
            description={dockNames.length ? dockNames.join(', ') : '—'}
          />
        ) : null}
      </>
    );
  }

  const handleChange = (values: MultiValue<DockOption>) => {
    setSelection(values.map(({ value }) => value));
  };

  const save = async () => {
    if (isBusy) return;
    const saved = await onSave(selection);
    if (saved) setEditing(false);
  };

  return (
    <div className="inline-flex min-w-[260px] items-center gap-1">
      <ReactSelect<DockOption, true>
        inputId={`docks-${entry.id}`}
        aria-label={`Doca de ${entry.plate}`}
        options={options}
        value={options.filter(({ value }) => selection.includes(value))}
        onChange={handleChange}
        isMulti
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        isDisabled={isBusy}
        menuPortalTarget={document.body}
        styles={dockSelectStyle}
        placeholder="Selecione"
      />
      <Button
        type="button"
        size="sm"
        variant="ghost"
        aria-label="Salvar"
        title="Salvar Doca"
        onClick={save}
        disabled={isBusy}
      >
        <Save size={16} aria-hidden="true" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        aria-label="Cancelar"
        title="Cancelar edição da Doca"
        onClick={() => {
          setSelection(previousSelection.current);
          setEditing(false);
        }}
        disabled={isBusy}
      >
        <X size={16} aria-hidden="true" />
      </Button>
    </div>
  );
};
